import React, { useState, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { IoMdAdd } from "react-icons/io";
import { useQuery } from "../../../hooks";
import { useDispatch, useSelector } from "react-redux";
import { TableContainer, Button, InputGroup } from "../../../UI";
import { getAllUsers } from "../../../redux/actions";
import { dashboardRoute } from "../../../constants";
import { columns } from "./tableData";
import ModalContent from "./ModalContent";
import Container from "./style";

const initFilterState = {
  userType: "",
  gender: "",
  location_id: "",
  search: "",
  page: 1,
};

const ManageUser = () => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const modal = useQuery().get("modal");

  const [filterState, setFilterState] = useState(initFilterState);
  const { userType, location_id, gender, search, page } = filterState;

  const { users, gymLocations } = useSelector((state) => state.dashStats);
  const history = useHistory();
  const [{ activeIndex, loading }, setState] = useState({
    activeIndex: null,
    loading: false,
  });

  const fetchUsers = useCallback(() => {
    let queries = [];

    if (userType) {
      queries.push(`status=${userType}`);
    }

    if (gender) {
      queries.push(`gender=${gender}`);
    }

    if (location_id !== "") {
      queries.push(`location_id=${location_id}`);
    }

    if (page && page > 1) {
      queries.push(`page=${page}`);
    }

    if (search && search.trim()) {
      queries.push(`search=${encodeURIComponent(search.trim())}`);
    }

    setState((s) => ({
      ...s,
      loading: true,
    }));

    const queryString = queries.length > 0 ? `?${queries.join("&")}` : "";

    dispatch(getAllUsers(queryString, true)).finally(() => {
      setState((s) => ({
        ...s,
        loading: false,
      }));
    });
  }, [dispatch, userType, gender, location_id, search, page]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const actionHandler = (index) => {
    setState((s) => ({ ...s, activeIndex: index }));
  };

  const clickHandler = useCallback(() => {
    if (activeIndex || activeIndex === 0) {
      setState((s) => ({ ...s, activeIndex: null }));
    }
  }, [activeIndex]);

  useEffect(() => {
    document.addEventListener("click", clickHandler);
    return () => {
      document.removeEventListener("click", clickHandler);
    };
  }, [clickHandler]);

  const handleInput = ({ target: { name, value } }) => {
    setFilterState((s) => ({
      ...s,
      [name]: value,
      page: 1,
    }));
  };

  const handleSearch = useCallback((searchVal) => {
    setFilterState((s) => {
      if (s.search === searchVal && s.page === 1) return s;
      return {
        ...s,
        search: searchVal,
        page: 1,
      };
    });
  }, []);

  const isActive = userType || gender || location_id || search;

  return (
    <Container>
      {modal === "addUser" && <ModalContent {...{ action: modal }} />}
      <header className="component-header">
        <h1>All Users</h1>
        <div className="header-row">
          <div className="action-group">
            <InputGroup
              type="select"
              value={gender}
              disabled={loading}
              name="gender"
              onChange={handleInput}
              optionLists={
                <>
                  <option value="" disabled>
                    Select gender...
                  </option>
                  {["M", "F"].map((d) => (
                    <option value={d} key={d}>
                      {d === "M" ? "Male" : "Female"}
                    </option>
                  ))}
                </>
              }
            />
            <InputGroup
              type="select"
              name="userType"
              value={userType}
              disabled={loading}
              onChange={handleInput}
              optionLists={
                <>
                  <option value="" disabled>
                    Select Users...
                  </option>
                  {["Active", "In-Active"].map((d) => (
                    <option value={d} key={d}>
                      {d}
                    </option>
                  ))}
                </>
              }
            />
            <InputGroup
              type="select"
              name="location_id"
              value={location_id}
              disabled={loading}
              onChange={handleInput}
              optionLists={
                <>
                  <option value="" disabled>
                    Select Location...
                  </option>
                  {Object.keys(gymLocations).map((locationId) => (
                    <option value={locationId} key={locationId}>
                      {gymLocations[locationId]}
                    </option>
                  ))}
                </>
              }
            />

            <Button
              style={{
                visibility: isActive ? "visible" : "hidden",
                pointerEvent: isActive ? "auto" : "none",
              }}
              onClick={() => {
                setFilterState(initFilterState);
              }}
            >
              Clear Filters
            </Button>
          </div>
          <div className="lastCol">
            <Button onClick={() => history.push(`${pathname}?modal=addUser`)}>
              <IoMdAdd />
              Add New
            </Button>
          </div>
        </div>
      </header>
      <TableContainer
        {...{
          columns: columns({
            gymLocations,
            handleViewProfile: (row) => {
              history.push(
                `${dashboardRoute.admin.userProfile.link}/${row.id}`
              );
            },
            handleEditProfile: (row) => {
              history.push(
                `${dashboardRoute.admin.userProfile.link}?modal=addUser&userId=${row.id}`
              );
            },
            handleViewOrder: (row) => {
              history.push(
                `${dashboardRoute.admin.userProfile.link}/${row.id}/orders`
              );
            },
            activeIndex,
            actionHandler,
            viewHandler: ({ id }) => {
              history.push(`/admin/dashboard/creators/${id}/details`);
            },
          }),
          searchValue: search,
          searchHandler: handleSearch,

          dataSource: users.data,
          activeIndex,
          loading: loading,
          pagination: {
            pageSize: users?.meta?.per_page || 20,
            current: users?.meta?.current_page || page,
            total: users?.meta?.total,
            onChange: (newPage) => {
              setFilterState((s) => ({
                ...s,
                page: newPage,
              }));
            },
          },
        }}
      />
    </Container>
  );
};

export default ManageUser;
